import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import ProductCompareStore from '../../../../stores/product-compare-store.js';
import measureHeight from '../../../../utils/measure-height.js';
import style from './product-compare-table.css';

export default class ProductCompareTable extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true });
    }

    properties() {
        return {
            columns: [],
            maxVisible: 4,
            canScrollLeft: false,
            canScrollRight: false,
            productCompareStore: new ProductCompareStore(),
        };
    }

    provideProperties() {
        return {
            productCompareStore: this.productCompareStore,
        };
    }

    connected() {
        this.productCompareStore.table = this;
        this._resizeObserver = new ResizeObserver(() => {
            this.productCompareStore.scheduleSyncHeights();
            requestAnimationFrame(() => this._updateScrollState());
        });
        this._resizeObserver.observe(this);
    }

    disconnected() {
        this._resizeObserver?.disconnect();
        this.productCompareStore.table = null;
    }

    _updateScrollState() {
        const container = this.$refs.scrollContainer;
        if (!container) return;

        const SCROLL_THRESHOLD = 2;
        const { scrollLeft, scrollWidth, clientWidth } = container;
        this.canScrollLeft = scrollLeft > SCROLL_THRESHOLD;
        this.canScrollRight = scrollLeft + clientWidth < scrollWidth - SCROLL_THRESHOLD;

        this._updateLastVisible(container);
    }

    _updateLastVisible(container) {
        const items = this.productCompareStore.items;
        const containerRight = container.getBoundingClientRect().right;
        const SNAP_TOLERANCE = 2;

        items.forEach((item) => item.removeAttribute('last-visible'));

        for (let i = items.length - 1; i >= 0; i--) {
            const rect = items[i].getBoundingClientRect();
            if (rect.right <= containerRight + SNAP_TOLERANCE && rect.left < containerRight) {
                items[i].setAttribute('last-visible', '');
                break;
            }
        }
    }

    _scrollBy(direction) {
        const container = this.$refs.scrollContainer;
        if (!container) return;

        const itemCount = this.productCompareStore.items.length;
        const visibleCount = Math.min(itemCount, this.maxVisible);
        const scrollAmount = container.clientWidth / visibleCount;

        container.scrollBy({ left: direction * scrollAmount });
    }

    resetSidebarHeights() {
        this.$refs.sidebarRows?.forEach((row) => (row.style.minHeight = ''));
    }

    getSidebarMeasurements() {
        return (this.$refs.sidebarRows || []).map((row) => measureHeight(row));
    }

    applySidebarHeights(rowHeights) {
        this.$refs.sidebarRows?.forEach((row, index) => {
            if (rowHeights[index]) row.style.minHeight = `${rowHeights[index]}px`;
        });
    }

    events() {
        return {
            '.scroll-container': {
                scroll: () => this._updateScrollState(),
            },
            '[ref="prevArrow"]': {
                click: () => this._scrollBy(-1),
            },
            '[ref="nextArrow"]': {
                click: () => this._scrollBy(1),
            },
        };
    }

    template() {
        const { headerHeight, rowHeights } = this.productCompareStore;
        const itemCount = this.productCompareStore.items.length;
        const needsScroll = itemCount > this.maxVisible;
        const visibleColumns = Math.min(itemCount, this.maxVisible);
        const COLUMN_GAP = 4;
        const MOBILE_PEEK = 36;
        const visibleGaps = (visibleColumns - 1) * COLUMN_GAP;
        const mobileColumnBasis =
            itemCount <= 1
                ? '100%'
                : itemCount === 2
                  ? `calc((100% - ${COLUMN_GAP}px) / 2)`
                  : `calc((100% - ${COLUMN_GAP * 2}px - ${MOBILE_PEEK}px) / 2)`;

        const lastIndex = this.columns.length - 1;
        const arrowClasses =
            'arrow hidden @md/compare-table:block absolute top-[43px] z-[1] transition-opacity duration-150';

        return html`
            <div class="flex gap-4">
                <div class="hidden @md/compare-table:flex flex-col shrink-0 w-[194px] min-w-[min-content]">
                    <div class="mb-fix-m" style="min-height: ${headerHeight ? `${headerHeight}px` : '0'};"></div>
                    <div class="flex flex-col">
                        ${this.columns.map(
                            (col, index) => html`
                                <div
                                    class="flex items-center px-16 py-8 fix-body2-medium text-green-medium bg-green-light25 border-b border-white ${index ===
                                    0
                                        ? 'rounded-tl-[8px]'
                                        : ''} ${index === lastIndex ? 'rounded-bl-[8px] border-b-0' : ''}"
                                    ref="sidebarRows[]"
                                    style="min-height: ${rowHeights[index] ? `${rowHeights[index]}px` : '0'};"
                                >
                                    ${col.label}
                                </div>
                            `,
                        )}
                    </div>
                </div>
                <div
                    class="scroll-container flex flex-1 min-w-0 overflow-x-auto scroll-smooth ${itemCount > 2
                        ? 'bleed'
                        : ''}"
                    ref="scrollContainer"
                    style="--visible-columns: ${visibleColumns}; --visible-gaps: ${visibleGaps}px; --mobile-column-basis: ${mobileColumnBasis};"
                >
                    <slot></slot>
                </div>
            </div>
            ${when(
                needsScroll,
                html`
                    <mdx-icon-button
                        class="${arrowClasses} arrow-prev ${this.canScrollLeft ? '' : 'opacity-0 pointer-events-none'}"
                        ref="prevArrow"
                        rounded
                        variant="primary"
                        size="md"
                        aria-label="Previous"
                    >
                        <mdx-icon-arrow-left></mdx-icon-arrow-left>
                    </mdx-icon-button>
                    <mdx-icon-button
                        class="${arrowClasses} arrow-next ${this.canScrollRight ? '' : 'opacity-0 pointer-events-none'}"
                        ref="nextArrow"
                        rounded
                        variant="primary"
                        size="md"
                        aria-label="Next"
                    >
                        <mdx-icon-arrow-right></mdx-icon-arrow-right>
                    </mdx-icon-button>
                `,
            )}
        `;
    }
}

defineElement('mb-product-compare-table', ProductCompareTable);
